<template>
  <div>
    <!-- リスト形式ビュー - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-list subheader nav v-if="layout === 'list'">
      <div
          v-for="(group, index) in groupView"
          :key="group.name">
        <v-subheader v-if="!mini">{{ group.name }}</v-subheader>
        <v-divider :style="{
          margin: index === 0 ? '5px 0' : '20px 0',
          borderColor: index === 0 ? 'transparent' : ''
        }" v-if="mini"></v-divider>
        <v-list-item
          v-for="item in group.items"
          :key="item.name"
          link
          :to="item.location"
          :dark="item.isAncestorRoute"
          :color="item.isAncestorRoute ? 'primary' : undefined"
          :class="{'v-list-item--active': item.isAncestorRoute}">
          <v-list-item-icon v-if="item.icon">
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </div>
    </v-list>

    <!-- グリッド形式ビュー - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <div v-else>
      <div
          v-for="group in groupView"
          :key="group.name">
        <v-subheader>{{ group.name }}</v-subheader>
        <v-row justify="start">
          <v-col
              v-for="item in group.items"
              :key="item.name"
              :cols="gridCols">
            <v-btn block outlined :to="item.location">
              <v-icon left v-if="item.icon">{{ item.icon }}</v-icon>
              {{ item.name }}
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script>
import PathInfoMixin from "@/router/PathInfoMixin";
import {mapGetters} from "vuex";
import PagePermissionMixin from "@/utils/PermissionMixin";
import cloneDeep from "clone-deep";
import {safeMeta, safeName} from "@/router/Meta";
import PageStoreMixin, {mapPageStores} from "@/utils/PageStoreMixin";

function isEmptyObject(mayEmptyObject) {
  return Object.keys(mayEmptyObject).length === 0 && mayEmptyObject.constructor === Object
}

function buildComputeDictionaryLocation(name) {
  const substitute = action => name.replace(/\{action\}/, action);
  return function() {
    let n = substitute(this.dictionaryAction);
    if(this.hasPagePermission(n, this.dictionaryAction)) {
      return n;
    } else {
      return substitute('browse');
    }
  };
}

export default {
  name: "NavigationList",
  mixins: [
    PathInfoMixin,
    PagePermissionMixin,
    PageStoreMixin
  ],
  props: {
    list: {
      type: Boolean,
      default: true
    },
    grid: {
      type: Boolean,
      default: false
    },
    gridCols: {
      type: Number,
      default: 4
    },
    mini: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      account: 'account'
    }),
    ...mapPageStores({
      dictionaryAction: {alt: 'edit', key: 'dictionary.action'},
    }),
    layout() {
      if(this.grid || !this.list) {
        return 'grid';
      } else {
        return 'list';
      }
    },
    groups() {
      return [{
        id: 'easy',
        name: 'かんたんモード',
        items: [{
          name: 'かんたんモード',
          icon: 'mdi-emoticon-happy-outline',
          location: {
            name: '/easy'
          }
        }]
      }, {
        id: 'trade',
        name: '取引管理',
        items: [{
          name: '取引契約管理',
          icon: 'icon-trade-contact',
          location: {
            name: '/edit/tradeContract/list'
          }
        }, {
          name: '取引実績管理',
          icon: 'icon-trade-result',
          location: {
            name: '/edit/list/tradeResult/TradeResultList'
          }
        }, {
          name: 'フォーマット管理',
          icon: 'mdi-folder-lock-outline',
          location: {
            name: '/browse/tradeFormat/list'
          }
        }],
      }, {
        id: 'profile',
        name: 'プロファイル',
        items: [{
          name: 'プロファイル管理',
          icon: 'mdi-home-search',
          location: {
            name: '/profileManagement',
          }
        }, {
          name: 'プロファイル照会',
          icon: 'icon-profile',
          location: {
            name: '/profileSearch'
          }
        }]
      }, {
        id: 'implementation',
        name: '実装管理',
        items: [{
          name: 'サイト実装管理',
          icon: 'icon-controller',
          location: {
            name: '/edit/list/terminalImplementation/TerminalImplementationList'
          }
        }, {
          name: 'サービス実装管理',
          icon: 'icon-service_implementation',
          location: {
            name: '/edit/list/serviceImplementation/ServiceImplementationList'
          }
        }, {
          name: 'データ実装管理',
          icon: 'icon-data-implementation',
          location: {
            name: '/edit/list/dataImplementation/DataImplementationList'
          }
        }]
      }, {
        id: 'dictionary',
        name: '辞書管理',
        items: [{
          name: '共通辞書一覧',
          icon: 'icon-common-dictionary',
          location: {
            name: this.commonDictionaryLocation
          }
        }, {
          name: '個別辞書一覧',
          icon: 'icon-local-dictionary',
          location: {
            name: this.localDictionaryLocation
          }
        }, {
          name: '外部辞書一覧',
          icon: 'icon-external-dictionary',
          location: {
            name: this.externalDictionaryLocation
          }
        }]
      }, {
        id: 'owner',
        name: '事業者管理',
        items: [{
          name: '事業者管理',
          icon: 'mdi-account-supervisor-circle',
          location: {
            name: '/admin/owner/OwnerShow'
          }
        }, {
          name: 'サイト管理',
          icon: 'mdi-cube-outline',
          location: {
            name: '/admin/terminal/TerminalList'
          }
        }, {
          name: 'ユーザ管理',
          icon: 'mdi-account',
          location: {
            name: '/admin/user/UserList'
          }
        }]
      }];
    },
    groupView() {
      return this.groups
          .map(group => ({
            ...group,
            items: group.items
                .map(item => {
                  if(isEmptyObject(item.location) || !item.location) {
                    item.location = {};
                    return item;
                  }
                  item = cloneDeep(item);
                  item.location = this.hasFallbackPage(item.location)?.location || {};
                  item.isAncestorRoute = this.isAncestorRoute(item.location);
                  return item;
                })
                .filter(item => !!item.location)
          }))
          .filter(group => {
            for(let item of group.items) {
              if(item.location && !isEmptyObject(item.location)) {
                return true;
              }
            }
            return false;
          });
    },
    currentAction() {
      if(this.action) {
        return this.action;
      } else {
        return 'browse';
      }
    },
    commonDictionaryLocation: buildComputeDictionaryLocation('/{action}/common/dictionary/DictionaryList'),
    localDictionaryLocation: buildComputeDictionaryLocation('/{action}/local/dictionary/DictionaryList'),
    externalDictionaryLocation: buildComputeDictionaryLocation('/{action}/external/dictionary/DictionaryList'),
  },
  methods: {
    isAncestorRoute(route) {
      let current = this.$route;
      while (safeName(current) !== 'Home') {
        const currentMeta = safeMeta(current, true);
        if(safeName(current) === route.name) {
          return true;
        }
        if(!currentMeta.parent) {
          break;
        }
        ({route: current} = this.$router.resolve({name: currentMeta.parent}));
      }
      return false;
    },
  },
}
</script>

<style scoped>
.v-subheader {
  overflow: hidden;
  white-space: nowrap;
}
</style>
